import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import CmsPageLayout from "../components/CmsPageLayout";
import CmsPageRoot from "../components/CmsPageRoot";
import CmsPageShell from "../components/CmsPageShell";
import ValidationSummary from "../components/ValidationSummary";
import StickyFormActions from "../components/StickyFormActions";
import PreviewJsonModal from "../components/PreviewJsonModal";
import { CMS_LANGUAGES, CMS_PAGE_META, DEFAULT_SOLUTIONS } from "../constants";
import { mergeSolutionsContent } from "../utils/mergeSolutionsContent";
import { normalizeSolutionsSectionIcons } from "../utils/cmsImageAsset";
import { validateSolutionsPageBilingual } from "../utils/validation";
import { applyCmsValidationFailure, scrollToFirstValidationError } from "../utils/validationUi";
import { fetchPageContent, saveSolutionsContent, uploadMedia } from "../api/cmsApi";
import { useCmsAuth } from "../hooks/useCmsAuth";
import { MarketingHeroSection, ProgramCtaSection } from "../sections/SharedCmsSections";
import {
  SmartSolutionsSection,
  AlternatingFeaturesSection,
  IntegratedDevicesSection,
} from "../sections/SolutionsSections";

const PAGE_META = CMS_PAGE_META.solutions;
const EN_ID = CMS_LANGUAGES.en.id;
const ES_ID = CMS_LANGUAGES.es.id;
const cloneSolutions = () => JSON.parse(JSON.stringify(DEFAULT_SOLUTIONS));

const SOLUTIONS_SECTION_MAP = [
  { id: "hero", prefixes: ["hero."] },
  { id: "smartSolutions", prefixes: ["smartSolutions."] },
  { id: "alternatingFeatures", prefixes: ["alternatingFeatures."] },
  { id: "integratedDevices", prefixes: ["integratedDevices."] },
  { id: "programCta", prefixes: ["programCta."] },
];

function SolutionsPageEditor() {
  const { token } = useCmsAuth();
  const [solutionsEn, setSolutionsEn] = useState(() => cloneSolutions());
  const [solutionsEs, setSolutionsEs] = useState(() => cloneSolutions());
  const [initialEn, setInitialEn] = useState(() => cloneSolutions());
  const [initialEs, setInitialEs] = useState(() => cloneSolutions());
  const [updatedAtEn, setUpdatedAtEn] = useState(null);
  const [updatedAtEs, setUpdatedAtEs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [previewOpen, setPreviewOpen] = useState(false);
  const [uploadingKey, setUploadingKey] = useState(null);

  const dirtyEn = JSON.stringify(solutionsEn) !== JSON.stringify(initialEn);
  const dirtyEs = JSON.stringify(solutionsEs) !== JSON.stringify(initialEs);
  const dirty = dirtyEn || dirtyEs;

  const loadAll = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setErrors({});
    try {
      const [enResult, esResult] = await Promise.all([
        fetchPageContent(token, EN_ID),
        fetchPageContent(token, ES_ID),
      ]);
      const enMerged = normalizeSolutionsSectionIcons(
        mergeSolutionsContent(cloneSolutions(), enResult.content.solutions)
      );
      const esMerged = normalizeSolutionsSectionIcons(
        mergeSolutionsContent(cloneSolutions(), esResult.content.solutions)
      );
      setSolutionsEn(enMerged);
      setSolutionsEs(esMerged);
      setInitialEn(JSON.parse(JSON.stringify(enMerged)));
      setInitialEs(JSON.parse(JSON.stringify(esMerged)));
      setUpdatedAtEn(enResult.updatedAt);
      setUpdatedAtEs(esResult.updatedAt);
    } catch (err) {
      const fallback = cloneSolutions();
      setSolutionsEn(fallback);
      setSolutionsEs(cloneSolutions());
      setInitialEn(JSON.parse(JSON.stringify(fallback)));
      setInitialEs(JSON.parse(JSON.stringify(cloneSolutions())));
      toast.error("Could not load from server. Restore CMS data in MongoDB.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const handleSave = async () => {
    const validationErrors = validateSolutionsPageBilingual(solutionsEn, solutionsEs);
    if (Object.keys(validationErrors).length) {
      applyCmsValidationFailure({
        validationErrors,
        setErrors,
        sectionPrefixMap: SOLUTIONS_SECTION_MAP,
        toastFn: (message) => toast.error(message),
      });
      return;
    }
    setSaving(true);
    try {
      const [enResult, esResult] = await Promise.all([
        saveSolutionsContent(token, EN_ID, solutionsEn),
        saveSolutionsContent(token, ES_ID, solutionsEs),
      ]);
      setInitialEn(JSON.parse(JSON.stringify(solutionsEn)));
      setInitialEs(JSON.parse(JSON.stringify(solutionsEs)));
      setErrors({});
      setUpdatedAtEn(enResult.updatedAt || new Date().toISOString());
      setUpdatedAtEs(esResult.updatedAt || new Date().toISOString());
      toast.success("Saved — live on the website (English & Spanish).");
    } catch (err) {
      toast.error("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (lang, key, file, onSuccess) => {
    setUploadingKey(`${lang}.${key}`);
    try {
      const publicUrl = await uploadMedia(token, file);
      if (publicUrl) {
        onSuccess(publicUrl);
        toast.success("Image uploaded.");
      } else {
        toast.error("Upload failed.");
      }
    } catch (err) {
      toast.error("Upload failed.");
    } finally {
      setUploadingKey(null);
    }
  };

  const sectionProps = {
    solutionsEn,
    solutionsEs,
    setSolutionsEn,
    setSolutionsEs,
    errors,
    onImageUpload: handleImageUpload,
    uploadingKey,
  };

  return (
    <CmsPageRoot>
      <CmsPageLayout
        actions={
          <StickyFormActions
            dirty={dirty}
            saving={saving}
            onSave={handleSave}
            onPreview={() => setPreviewOpen(true)}
          />
        }
      >
      <CmsPageShell
        pageIcon={PAGE_META.pageIcon}
        pageIconSrc={PAGE_META.tabIcon}
        title={PAGE_META.title}
        subtitle={PAGE_META.subtitle}
        dirtyEn={dirtyEn}
        dirtyEs={dirtyEs}
        updatedAtEn={updatedAtEn}
        updatedAtEs={updatedAtEs}
        loading={loading}
        skeletonPage="solutions"
      >
        <ValidationSummary
          errors={errors}
          onJumpToFirst={() =>
            scrollToFirstValidationError(errors, { sectionPrefixMap: SOLUTIONS_SECTION_MAP })
          }
        />
        <MarketingHeroSection
          id="hero"
          title="Hero"
          description="Top banner"
          heroEn={solutionsEn.hero || {}}
          heroEs={solutionsEs.hero || {}}
          setHeroEn={(hero) => setSolutionsEn({ ...solutionsEn, hero })}
          setHeroEs={(hero) => setSolutionsEs({ ...solutionsEs, hero })}
          {...sectionProps}
        />
        <SmartSolutionsSection {...sectionProps} />
        <AlternatingFeaturesSection {...sectionProps} />
        <IntegratedDevicesSection {...sectionProps} />
        <ProgramCtaSection
          ctaEn={solutionsEn.programCta || {}}
          ctaEs={solutionsEs.programCta || {}}
          setCtaEn={(programCta) => setSolutionsEn({ ...solutionsEn, programCta })}
          setCtaEs={(programCta) => setSolutionsEs({ ...solutionsEs, programCta })}
          {...sectionProps}
        />
      </CmsPageShell>
      </CmsPageLayout>
      <PreviewJsonModal
        isOpen={previewOpen}
        toggle={() => setPreviewOpen(false)}
        title="Solutions page payload"
        payload={{ en: { solutions: solutionsEn }, es: { solutions: solutionsEs } }}
      />
    </CmsPageRoot>
  );
}

export default SolutionsPageEditor;
